import type { app__schema__subscriber__Add } from '../models/app__schema__subscriber__Add';
import type { app__schema__subscriber__Get } from '../models/app__schema__subscriber__Get';
import type { NewsletterPreviewResponse } from '../models/NewsletterPreviewResponse';
import type { NewsletterSendStatus } from '../models/NewsletterSendStatus';
import type { NewsletterTemplate } from '../models/NewsletterTemplate';
import type { NewsletterTestMail } from '../models/NewsletterTestMail';
import type { Subscriber } from '../models/Subscriber';
import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export type TDataDeleteSubscriberByEmailApiV1NewsletterSubscriberEmailDelete = {
                email: string
            }
export type TDataGetSubscriberByEmailApiV1NewsletterSubscriberEmailGet = {
                email: string
            }
export type TDataAddSubscriberApiV1NewsletterSubscriberPost = {
                requestBody: app__schema__subscriber__Add
            }
export type TDataSaveNewsletterTemplateApiV1NewsletterTemplatePut = {
                requestBody: NewsletterTemplate
            }
export type TDataPreviewNewsletterTemplateApiV1NewsletterPreviewPost = {
                requestBody: NewsletterTemplate
            }
export type TDataSendNewsletterTestMailApiV1NewsletterTestPost = {
                requestBody: NewsletterTestMail
            }
export type TDataUploadNewsletterAttachmentApiV1NewsletterTemplateAttachmentsPost = {
                formData: {
                    file: Blob;
                }
            }
export type TDataDeleteNewsletterAttachmentApiV1NewsletterTemplateAttachmentsAttachmentIdDelete = {
                attachmentId: string
            }

export class NewsletterService {

	/**
	 * Get Newsletter Template
	 * Get the saved newsletter template
	 * @returns NewsletterTemplate Successful Response
	 * @throws ApiError
	 */
	public static getNewsletterTemplateApiV1NewsletterTemplateGet(): CancelablePromise<NewsletterTemplate> {
				return __request(OpenAPI, {
			method: 'GET',
			url: '/api/v1/newsletter/template',
		});
	}

	/**
	 * Save Newsletter Template
	 * Save the newsletter template
	 * @returns NewsletterTemplate Successful Response
	 * @throws ApiError
	 */
	public static saveNewsletterTemplateApiV1NewsletterTemplatePut(data: TDataSaveNewsletterTemplateApiV1NewsletterTemplatePut): CancelablePromise<NewsletterTemplate> {
		const {
requestBody,
} = data;
		return __request(OpenAPI, {
			method: 'PUT',
			url: '/api/v1/newsletter/template',
			body: requestBody,
			mediaType: 'application/json',
			errors: {
				422: `Validation Error`,
			},
		});
	}

	/**
	 * Upload Newsletter Attachment
	 * Upload a PDF attachment for the newsletter template
	 * @returns NewsletterTemplate Successful Response
	 * @throws ApiError
	 */
	public static uploadNewsletterAttachmentApiV1NewsletterTemplateAttachmentsPost(data: TDataUploadNewsletterAttachmentApiV1NewsletterTemplateAttachmentsPost): CancelablePromise<NewsletterTemplate> {
		const {
formData,
} = data;
		const body = new FormData();
		body.append('file', formData.file);
		return __request(OpenAPI, {
			method: 'POST',
			url: '/api/v1/newsletter/template/attachments',
			body,
			errors: {
				422: `Validation Error`,
			},
		});
	}

	/**
	 * Delete Newsletter Attachment
	 * Remove a PDF attachment from the newsletter template
	 * @returns NewsletterTemplate Successful Response
	 * @throws ApiError
	 */
	public static deleteNewsletterAttachmentApiV1NewsletterTemplateAttachmentsAttachmentIdDelete(data: TDataDeleteNewsletterAttachmentApiV1NewsletterTemplateAttachmentsAttachmentIdDelete): CancelablePromise<NewsletterTemplate> {
		const {
attachmentId,
} = data;
		return __request(OpenAPI, {
			method: 'DELETE',
			url: '/api/v1/newsletter/template/attachments/{attachment_id}',
			path: {
				attachment_id: attachmentId,
			},
			errors: {
				422: `Validation Error`,
			},
		});
	}

	/**
	 * Preview Newsletter Template
	 * Render a newsletter preview without saving
	 * @returns NewsletterPreviewResponse Successful Response
	 * @throws ApiError
	 */
	public static previewNewsletterTemplateApiV1NewsletterPreviewPost(data: TDataPreviewNewsletterTemplateApiV1NewsletterPreviewPost): CancelablePromise<NewsletterPreviewResponse> {
		const {
requestBody,
} = data;
		return __request(OpenAPI, {
			method: 'POST',
			url: '/api/v1/newsletter/preview',
			body: requestBody,
			mediaType: 'application/json',
			errors: {
				422: `Validation Error`,
			},
		});
	}

	/**
	 * Send Newsletter Test Mail
	 * Send a test newsletter to the given email address
	 * @returns unknown Successful Response
	 * @throws ApiError
	 */
	public static sendNewsletterTestMailApiV1NewsletterTestPost(data: TDataSendNewsletterTestMailApiV1NewsletterTestPost): CancelablePromise<unknown> {
		const {
requestBody,
} = data;
		return __request(OpenAPI, {
			method: 'POST',
			url: '/api/v1/newsletter/test',
			body: requestBody,
			mediaType: 'application/json',
			errors: {
				422: `Validation Error`,
			},
		});
	}

	/**
	 * Get Newsletter Send Status
	 * Check whether a bulk newsletter send is currently in progress
	 * @returns NewsletterSendStatus Successful Response
	 * @throws ApiError
	 */
	public static getNewsletterSendStatusApiV1NewsletterSendStatusGet(): CancelablePromise<NewsletterSendStatus> {
				return __request(OpenAPI, {
			method: 'GET',
			url: '/api/v1/newsletter/send-status',
		});
	}

	/**
	 * Cancel Newsletter Send
	 * Request cancellation of an in-progress bulk newsletter send
	 * @returns unknown Successful Response
	 * @throws ApiError
	 */
	public static cancelNewsletterSendApiV1NewsletterSendCancelPost(): CancelablePromise<unknown> {
				return __request(OpenAPI, {
			method: 'POST',
			url: '/api/v1/newsletter/send-cancel',
		});
	}

	/**
	 * Send Newsletter To Subscribers
	 * Send a newsletter
	 * @returns unknown Successful Response
	 * @throws ApiError
	 */
	public static sendNewsletterToSubscribersApiV1NewsletterPost(): CancelablePromise<unknown> {
				return __request(OpenAPI, {
			method: 'POST',
			url: '/api/v1/newsletter',
		});
	}

	/**
	 * Delete Subscriber By Email
	 * Delete a subscriber from newsletter
	 * @returns app__schema__subscriber__Get Successful Response
	 * @throws ApiError
	 */
	public static deleteSubscriberByEmailApiV1NewsletterSubscriberEmailDelete(data: TDataDeleteSubscriberByEmailApiV1NewsletterSubscriberEmailDelete): CancelablePromise<app__schema__subscriber__Get> {
		const {
email,
} = data;
		return __request(OpenAPI, {
			method: 'DELETE',
			url: '/api/v1/newsletter/subscriber/{email}',
			path: {
				email
			},
			errors: {
				422: `Validation Error`,
			},
		});
	}

	/**
	 * Get Subscriber By Email
	 * Get subscriber by mail address
	 * @returns app__schema__subscriber__Get Successful Response
	 * @throws ApiError
	 */
	public static getSubscriberByEmailApiV1NewsletterSubscriberEmailGet(data: TDataGetSubscriberByEmailApiV1NewsletterSubscriberEmailGet): CancelablePromise<app__schema__subscriber__Get> {
		const {
email,
} = data;
		return __request(OpenAPI, {
			method: 'GET',
			url: '/api/v1/newsletter/subscriber/{email}',
			path: {
				email
			},
			errors: {
				422: `Validation Error`,
			},
		});
	}

	/**
	 * Add Subscriber
	 * Add a subscriber to newsletter
	 * @returns app__schema__subscriber__Get Successful Response
	 * @throws ApiError
	 */
	public static addSubscriberApiV1NewsletterSubscriberPost(data: TDataAddSubscriberApiV1NewsletterSubscriberPost): CancelablePromise<app__schema__subscriber__Get> {
		const {
requestBody,
} = data;
		return __request(OpenAPI, {
			method: 'POST',
			url: '/api/v1/newsletter/subscriber',
			body: requestBody,
			mediaType: 'application/json',
			errors: {
				422: `Validation Error`,
			},
		});
	}

	/**
	 * Get All Subscribers
	 * Get all subscribers
	 * @returns Subscriber Successful Response
	 * @throws ApiError
	 */
	public static getAllSubscribersApiV1NewsletterSubscribersGet(): CancelablePromise<Array<Subscriber>> {
				return __request(OpenAPI, {
			method: 'GET',
			url: '/api/v1/newsletter/subscribers',
		});
	}

}